import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Icon } from '@iconify/react'
import { get, post, deleteAPI } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import FormLoader from '../../../../components/loader/FormLoader'
import '../debts/Debts.scss'

interface DebtBatch {
  _id: string
  windowStart: string
  windowIndex: number
  totalAmount: number
  amountPaid: number
  remainingAmount: number
  deadline: string
  isOverdue: boolean
  isCleared: boolean
  bookingCount: number
}

interface DebtSummaryData {
  user: {
    _id: string
    name?: string
    org_name?: string
    firstname?: string
    lastname?: string
    email: string
    outstanding?: number
    debt?: number
    graceUntil?: string
    graceNote?: string
    graceGrantedAt?: string
  }
  summary: {
    totalOutstanding: number
    overdueAmount: number
    pendingAmount: number
    overdueBatchCount: number
    pendingBatchCount: number
    isBlocked: boolean
    nextDeadline: string
  }
  batches: DebtBatch[]
}

interface GrantGraceForm {
  graceUntil: string
  note: string
}

interface Props {
  userId: string
  userModel: 'User' | 'Business'
}

function DebtSummarySection({ userId, userModel }: Props) {
  const { notify, profile } = useGlobalContext()
  const navigate = useNavigate()

  const [data, setData] = useState<DebtSummaryData | null>(null)
  const [loading, setLoading] = useState(true)
  const [showGrantModal, setShowGrantModal] = useState(false)
  const [grantForm, setGrantForm] = useState<GrantGraceForm>({
    graceUntil: '',
    note: '',
  })
  const [submitting, setSubmitting] = useState(false)

  function fetchDebtSummary() {
    setLoading(true)
    get(`admin/debt/summary/${userId}?userModel=${userModel}`)
      .then((result: any) => {
        if (result.status === 200) {
          setData(result.data)
        } else {
          notify('error', result.message)
        }
        setLoading(false)
      })
      .catch(() => {
        setLoading(false)
        notify('error', 'Failed to fetch debt summary')
      })
  }

  useEffect(() => {
    if (userId) {
      fetchDebtSummary()
    }
  }, [userId, userModel])

  const handleGrantGrace = () => {
    if (!grantForm.graceUntil) {
      notify('error', 'Please select a grace period date')
      return
    }

    setSubmitting(true)
    post(
      `admin/debt/grace/${userId}`,
      {
        userModel: userModel,
        graceUntil: grantForm.graceUntil,
        note: grantForm.note,
      },
      null,
    )
      .then((result: any) => {
        if (result.status === 200 || result.status === 201) {
          notify('success', 'Grace period granted successfully')
          setShowGrantModal(false)
          setGrantForm({ graceUntil: '', note: '' })
          fetchDebtSummary()
        } else {
          notify('error', result.message)
        }
        setSubmitting(false)
      })
      .catch(() => {
        setSubmitting(false)
        notify('error', 'Failed to grant grace period')
      })
  }

  const handleRevokeGrace = () => {
    if (!confirm('Are you sure you want to revoke this grace period?')) {
      return
    }

    deleteAPI(`admin/debt/grace/${userId}?userModel=${userModel}`)
      .then((result: any) => {
        if (result.status === 200) {
          notify('success', 'Grace period revoked successfully')
          fetchDebtSummary()
        } else {
          notify('error', result.message)
        }
      })
      .catch(() => {
        notify('error', 'Failed to revoke grace period')
      })
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-NG', {
      dateStyle: 'medium',
      timeStyle: 'short',
    })
  }

  const getWindowName = (windowIndex: number) => {
    const windows = [
      '00:00 - 06:00',
      '06:00 - 12:00',
      '12:00 - 18:00',
      '18:00 - 00:00',
    ]
    return windows[windowIndex] || 'Unknown'
  }

  if (loading) {
    return <FormLoader position='center' />
  }

  if (!data) {
    return (
      <div className='empty flex justify-center'>
        <div className='empty-cover'>
          <Icon icon='mdi:alert-circle-outline' />
          <h1>No Debt Summary Available</h1>
        </div>
      </div>
    )
  }

  const isSuperAdminOrManager = profile.role === 'super admin'

  return (
    <div className='debt-summary-section'>
      {showGrantModal && (
        <div className='modal-overlay' onClick={() => setShowGrantModal(false)}>
          <div className='modal-content' onClick={(e) => e.stopPropagation()}>
            <div className='modal-header'>
              <h2>Grant Grace Period</h2>
              <Icon
                icon='mdi:close'
                onClick={() => setShowGrantModal(false)}
                style={{ cursor: 'pointer', fontSize: '24px' }}
              />
            </div>
            <div className='modal-body'>
              <div className='form-group'>
                <label>Grace Until *</label>
                <input
                  type='datetime-local'
                  value={grantForm.graceUntil}
                  onChange={(e) =>
                    setGrantForm({ ...grantForm, graceUntil: e.target.value })
                  }
                />
              </div>
              <div className='form-group'>
                <label>Note</label>
                <textarea
                  value={grantForm.note}
                  onChange={(e) =>
                    setGrantForm({ ...grantForm, note: e.target.value })
                  }
                  placeholder='Optional note about the grace period'
                  rows={3}
                />
              </div>
              <div className='modal-actions'>
                <button
                  className='btn-secondary'
                  onClick={() => setShowGrantModal(false)}
                  disabled={submitting}
                >
                  Cancel
                </button>
                <button
                  className='btn-primary'
                  onClick={handleGrantGrace}
                  disabled={submitting}
                >
                  {submitting ? 'Granting...' : 'Grant Grace'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className='debt-summary-compact'>
        <div className='summary-header'>
          <h3>Debt Overview</h3>
          <button
            className='view-full-btn'
            onClick={() =>
              navigate(`/admin/debt-summary/${userId}?userModel=${userModel}`)
            }
          >
            <Icon icon='mdi:open-in-new' />
            View Full Summary
          </button>
        </div>

        <div className='summary-stats-grid'>
          <div className='stat-card'>
            <p>Total Outstanding</p>
            <h3 className='total'>
              {formatCurrency(data.summary.totalOutstanding)}
            </h3>
          </div>
          <div className='stat-card'>
            <p>Overdue Amount</p>
            <h3 className='overdue'>
              {formatCurrency(data.summary.overdueAmount)}
            </h3>
          </div>
          <div className='stat-card'>
            <p>Pending Amount</p>
            <h3 className='pending'>
              {formatCurrency(data.summary.pendingAmount)}
            </h3>
          </div>
          <div className='stat-card'>
            <p>Overdue Batches</p>
            <h3 className='overdue'>{data.summary.overdueBatchCount}</h3>
          </div>
          <div className='stat-card'>
            <p>Pending Batches</p>
            <h3 className='pending'>{data.summary.pendingBatchCount}</h3>
          </div>
          <div className='stat-card'>
            <p>Status</p>
            <h3 className={data.summary.isBlocked ? 'overdue' : 'success'}>
              {data.summary.isBlocked ? 'BLOCKED' : 'Active'}
            </h3>
          </div>
        </div>

        <div className='grace-status-section'>
          {data.user.graceUntil ? (
            <div className='grace-active'>
              <Icon icon='mdi:shield-check' />
              <div className='grace-info'>
                <p>
                  <strong>Grace Period Active</strong>
                </p>
                <p>Until: {formatDate(data.user.graceUntil)}</p>
                {data.user.graceNote && <p>Note: {data.user.graceNote}</p>}
              </div>
              {isSuperAdminOrManager && (
                <button className='revoke-btn' onClick={handleRevokeGrace}>
                  Revoke
                </button>
              )}
            </div>
          ) : (
            <div className='grace-inactive'>
              <Icon icon='mdi:shield-off' />
              <p>No active grace period</p>
              {isSuperAdminOrManager && (
                <button
                  className='grant-btn'
                  onClick={() => setShowGrantModal(true)}
                >
                  Grant Grace Period
                </button>
              )}
            </div>
          )}
        </div>

        {data.batches.length > 0 && (
          <div className='batches-preview'>
            <h4>Recent Debt Batches ({data.batches.length})</h4>
            <div className='batches-table-wrapper'>
              <table>
                <thead>
                  <tr>
                    <th>Window</th>
                    <th>Total</th>
                    <th>Paid</th>
                    <th>Remaining</th>
                    <th>Deadline</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.batches.slice(0, 5).map((batch: DebtBatch) => (
                    <tr key={batch._id}>
                      <td>
                        <span className='window-badge'>
                          W{batch.windowIndex}
                        </span>
                        <br />
                        <small>{getWindowName(batch.windowIndex)}</small>
                      </td>
                      <td>{formatCurrency(batch.totalAmount)}</td>
                      <td className='success'>
                        {formatCurrency(batch.amountPaid)}
                      </td>
                      <td className={batch.isOverdue ? 'failed' : ''}>
                        {formatCurrency(batch.remainingAmount)}
                      </td>
                      <td>
                        <small>{formatDate(batch.deadline)}</small>
                      </td>
                      <td>
                        {batch.isCleared ? (
                          <span className='badge badge-success'>Cleared</span>
                        ) : batch.isOverdue ? (
                          <span className='badge badge-danger'>Overdue</span>
                        ) : (
                          <span className='badge badge-warning'>Pending</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {data.batches.length > 5 && (
              <p className='view-more'>
                Showing 5 of {data.batches.length} batches.{' '}
                <span
                  onClick={() =>
                    navigate(
                      `/admin/debt-summary/${userId}?userModel=${userModel}`,
                    )
                  }
                >
                  View all →
                </span>
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default DebtSummarySection
